import { yupResolver } from '@hookform/resolvers/yup'
import { useMemo, useState } from 'react'
import { Controller, useForm, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import * as Yup from 'yup'

import {
  AsyncSelect,
  ConfirmationResultModal,
  FormField,
  Select,
  Text,
  getInputStatus,
  type SelectOption
} from '@percent/lemonade'
import { useCountries } from '@percent/partner-dashboard/common/hooks/useCountries/useCountries'
import { useMutation, useOrganisationLookup } from '@percent/partner-dashboard/common/hooks'
import { useServices } from '@percent/partner-dashboard/context/serviceContext/ServiceContext'
import type { Organisation } from '@percent/partner-dashboard/services/nonprofits/nonprofitsService.types'

export type AddOrganisationToCategoryModalProps = Readonly<{
  categoryId: string
  open: boolean
  onClose: () => void
  onSuccess: () => void
}>

type SubmissionState = 'success' | 'failure'

type AddOrganisationFormValues = {
  country: string
  organisationId: string
  organisationName: string
}

const MIN_ORGANISATION_SEARCH_LENGTH = 3

const initialFormValues: AddOrganisationFormValues = {
  country: '',
  organisationId: '',
  organisationName: ''
}

export function AddOrganisationToCategoryModal({
  categoryId,
  open,
  onClose,
  onSuccess
}: AddOrganisationToCategoryModalProps) {
  const { t } = useTranslation()
  const { countries } = useCountries()
  const { categoryService } = useServices()
  const [modalState, setModalState] = useState<SubmissionState | undefined>(undefined)
  const [selectedOrganisation, setSelectedOrganisation] = useState<Organisation | null>(null)
  const selectedOrganisationLabel = selectedOrganisation?.displayName || selectedOrganisation?.name

  const countryOptions = useMemo(
    () => (countries ?? []).map(country => ({ value: country.code, label: country.name })),
    [countries]
  )

  const countryNamesByCode = useMemo(
    () => new Map<string, string>((countries ?? []).map(country => [country.code, country.name])),
    [countries]
  )

  const [{ isLoading: isSubmitting }, { apiFunc: addOrganisationToCategory }] = useMutation(
    categoryService.addOrganisationToCategory,
    () => setModalState('success'),
    () => setModalState('failure')
  )

  const validationSchema = Yup.object({
    country: Yup.string().required(t('errorMessage.required')),
    organisationId: Yup.string().required(t('errorMessage.required')),
    organisationName: Yup.string()
  })

  const { control, handleSubmit, setValue, reset } = useForm<AddOrganisationFormValues>({
    resolver: yupResolver(validationSchema),
    defaultValues: initialFormValues
  })

  const selectedCountry = useWatch({ control, name: 'country' })
  const selectedOrganisationName = useWatch({ control, name: 'organisationName' })

  const submit = handleSubmit(({ organisationId }) => addOrganisationToCategory({ categoryId, organisationId }))

  const { orgSearchQuery, setOrgSearchQuery, orgSearchData, isSearchDataForSelectedCountry, isOrgSearchLoading } =
    useOrganisationLookup(selectedCountry)

  const organisationOptions = useMemo(() => {
    if (!isSearchDataForSelectedCountry) {
      return []
    }

    return (orgSearchData ?? []).map(organisation => ({
      value: organisation.id,
      label: organisation.name,
      description: [
        organisation.website,
        organisation.registryId &&
          t('categories.addOrganisation.modal.organisation.registryId', { registryId: organisation.registryId }),
        countryNamesByCode.get(organisation.countryCode) ?? organisation.countryCode
      ]
        .filter(Boolean)
        .join(' | ')
    }))
  }, [orgSearchData, isSearchDataForSelectedCountry, countryNamesByCode, t])

  const handleClose = () => {
    if (modalState === 'success') {
      onSuccess()
    }

    reset(initialFormValues)
    setSelectedOrganisation(null)
    setOrgSearchQuery('')
    setModalState(undefined)
    onClose()
  }

  // Not validated here: clearing the organisation is a consequence of changing country, so flagging
  // it as missing before the user has had a chance to pick one would be premature.
  const clearSelectedOrganisation = () => {
    setValue('organisationId', '')
    setValue('organisationName', '')
    setSelectedOrganisation(null)
    setOrgSearchQuery('')
  }

  return (
    <ConfirmationResultModal
      isOpen={open}
      state={modalState}
      title={t('categories.addOrganisation.modal.title')}
      primaryButtonText={t('categories.addOrganisation.button')}
      secondaryButtonText={t('button.cancel')}
      primaryBtnTestId="add-organisation-to-category-submit"
      loading={isSubmitting}
      onConfirm={submit}
      onClose={handleClose}
      success={{
        title: t('categories.addOrganisation.modal.success.title'),
        description: t('categories.addOrganisation.modal.success.description', {
          organization: selectedOrganisationLabel
        }),
        button: t('button.close')
      }}
      failure={{
        title: t('categories.addOrganisation.modal.error.title'),
        description: t('categories.addOrganisation.modal.error.description', {
          organization: selectedOrganisationLabel
        }),
        button: t('button.close')
      }}
    >
      <form onSubmit={submit}>
        <Text>{t('categories.addOrganisation.modal.description')}</Text>
        <Controller
          control={control}
          name="country"
          render={({ field, fieldState }) => (
            <FormField
              label={t('categories.addOrganisation.modal.country')}
              necessity="required"
              status={getInputStatus(fieldState.error?.message)}
              statusMessage={fieldState.error?.message}
            >
              <Select
                name={field.name}
                placeholder={t('categories.addOrganisation.modal.country.placeholder')}
                options={countryOptions}
                defaultValue={countryOptions.find(option => option.value === field.value)}
                onChange={(option: SelectOption | null) => {
                  field.onChange(option?.value ?? '')
                  clearSelectedOrganisation()
                }}
                status={getInputStatus(fieldState.error?.message)}
                searchable
                showPrefixForSelectedOption
                key={field.value || 'no-country'}
              />
            </FormField>
          )}
        />
        <Controller
          control={control}
          name="organisationId"
          render={({ field, fieldState }) => (
            <FormField
              label={t('categories.addOrganisation.modal.organisation')}
              necessity="required"
              status={getInputStatus(fieldState.error?.message)}
              statusMessage={fieldState.error?.message}
            >
              <AsyncSelect
                name={field.name}
                key={selectedCountry || 'no-country'}
                placeholder={t('categories.addOrganisation.modal.organisation.placeholder')}
                onChange={(option: SelectOption | null) => {
                  field.onChange(option?.value ?? '')
                  setValue('organisationName', option?.label ?? '')
                  setSelectedOrganisation(orgSearchData?.find(org => org.id === option?.value) ?? null)
                }}
                options={organisationOptions}
                setQuery={setOrgSearchQuery}
                query={selectedOrganisationName || orgSearchQuery}
                loading={isOrgSearchLoading && !!selectedCountry}
                loadingText={t('typography.searching')}
                noResultsFoundText={t('typography.noSearchResults')}
                status={getInputStatus(fieldState.error?.message)}
                disabled={!selectedCountry}
                minSearchLength={MIN_ORGANISATION_SEARCH_LENGTH}
                minSearchLengthMessage={t('typography.minSearchLengthMessage', {
                  count: MIN_ORGANISATION_SEARCH_LENGTH
                })}
              />
            </FormField>
          )}
        />
      </form>
    </ConfirmationResultModal>
  )
}
